import gql from 'graphql-tag'

export const ElementPartsFragment = gql`
  fragment ElementParts on Project {
    id
    name
    number
    conditions_of_acceptance
    customer
    welding_progress
    inspection_progress {
      name
      count
      count_failed
      percent
      inspection_category_type_id
    }
    planned_start_date
    planned_end_date
    depth
    company_ids
    project_companies
    attachments {
      id
      data_file_name
      url
    }
    company_permitted
  }
`

export const ProjectPartsFragment = gql`
  fragment ProjectParts on Project {
    ...ElementParts
    extra_columns
    repair_welding_overlap
    repair_inspection_overlap
    require_attachments
  }
${ElementPartsFragment}
`

export const ProjectsListQuery = gql`
  query ProjectsListQuery($pageSize: Int = 10, $page: Int = 1, $name: String) {
    projects(page: $page, size: $pageSize, name: $name) {
      list {
        ...ProjectParts
      }
      metadata {
        current_page
        total_pages
      }
    }
    companies {
      list {
        id
        name
      }
    }
  }
${ProjectPartsFragment}
`

export const destroyProjectsMutation = gql`
  mutation destroyProjects($id: ID!) {
    destroyProjects(id: $id) {
      response
    }
  }
`

export const SAVE_PROJECT = gql`
  mutation createOrUpdateProjects($attributes: ProjectInput!) {
    createOrUpdateProjects(attributes: $attributes) {
      project {
        ...ProjectParts
      }
      errors
    }
  }
${ProjectPartsFragment}
`

export const destroyAttachmentsMutation = gql`
  mutation destroyAttachments($attachment_id: [Int]!, $attachable_id: Int!, $attachable_type: String!) {
    destroyAttachments(attachment_id: $attachment_id, attachable_id: $attachable_id, attachable_type: $attachable_type) {
      response
    }
  }
`

export const WpsAndInspectionsDictionariesQuery = gql`
  query WpsAndInspectionsDictionariesQuery($project_id: ID) {
    wps_list(project_id: $project_id) {
      list { 
        id
        no
        rev_no
        actual
      }
    }
    inspection_category_types {
      id
      name
    }
    type_of_welds {
      id
      name
    }
    joint_types {
      id
      name
    }
    welding_consumables {
      id
      name
    }
  }
`

export const ElementsListQuery = gql`
  query ElementsListQuery($parent_id: ID!) {
    element(id: $parent_id) {
      id
      name
      depth
      company_permitted
      extra_columns
      root_project {
        id
      }
      inspection_categories {
        id
        name
        hold_time
        inspections {
          type_id
          range
          double_length
        }
      }
      parents {
        id
        name
      }
    }
    companies {
      list {
        id
        name
      }
    }
  }
`

export const CreateExportWeldsList = gql`
  mutation createJobs($type: String = "WlWelds::Jobs::ExportWeldsList", $args: String) {
    createJobs(type: $type, args: $args) {
      job {
        id
      }
    }
  }
`

export const UserProjectConfigurationQuery = gql`
  query UserProjectConfigurationQuery($project_id: Int!, $type: String!) {
    user_project_configuration(project_id: $project_id, type: $type) {
      id
      configuration
    }
  }
`

export const CreateOrUpdateUserProjectConfiguration = gql`
  mutation createOrUpdateUserProjectConfiguration($attributes: UserProjectConfigurationInput!) {
    createOrUpdateUserProjectConfiguration(attributes: $attributes) {
      user_project_configuration {
        id
        configuration
      }
      errors
    }
  }
`

export const GroupingWeldsQuery = gql`
  query GroupingWeldsQuery($project_id: ID!) {
    grouping_welds(project_id: $project_id) {
      id
      no
      status
      length
      thickness_1
      thickness_2
      diameter_1
      diameter_2
      material_1 { id designation_name }
      material_2 { id designation_name }
      weld_joint_type { weld_type { id name } joint_type { id name } }
      inspection_category {
        id
        name
        inspections {
          type_id
          range
        }
      }
    }
    element(id: $project_id) {
      id
      min_grouping_welds_length
    }
  }
`

export const CreateWeldInspectionGroupsMutation = gql`
  mutation createWeldInspectionGroups(
    $project_id: Int!
    $inspection_category_type_id: Int!
    $group_by_keys: String!
    $min_welds: Int!
    $groups: [WeldInspectionGroupInput!]!
  ) {
    createWeldInspectionGroups(
      project_id: $project_id
      inspection_category_type_id: $inspection_category_type_id
      group_by_keys: $group_by_keys
      min_welds: $min_welds
      groups: $groups
    ) {
      weld_inspection_groups {
        id
        inspection_category_type_id
        scope_percent
        required_length
        representative_weld_id
        weld_ids
      }
      errors
    }
  }
`

export const WeldInspectionGroupsQuery = gql`
  query WeldInspectionGroupsQuery($project_id: Int!, $inspection_category_type_id: Int) {
    weld_inspection_groups(project_id: $project_id, inspection_category_type_id: $inspection_category_type_id) {
      id
      inspection_category_type_id
      group_by_keys
      min_welds
      representative_weld_id
      scope_percent
      required_length
      weld_ids
      welds_inspections
      welds_data
    }
  }
`

export const DestroyWeldInspectionGroupMutation = gql`
  mutation destroyWeldInspectionGroup($id: ID!) {
    destroyWeldInspectionGroup(id: $id)
  }
`

export const RemoveWeldFromInspectionGroupMutation = gql`
  mutation removeWeldFromInspectionGroup($group_id: ID!, $weld_id: ID!) {
    removeWeldFromInspectionGroup(group_id: $group_id, weld_id: $weld_id)
  }
`

export const DestroyAllWeldInspectionGroupsMutation = gql`
  mutation destroyAllWeldInspectionGroups($project_id: ID!, $inspection_category_type_id: Int!) {
    destroyAllWeldInspectionGroups(project_id: $project_id, inspection_category_type_id: $inspection_category_type_id)
  }
`

export const ProjectSettingsQuery = gql`
  query ProjectSettingsQuery($project_id: ID!) {
    element(id: $project_id) {
      id
      min_grouping_welds_length
    }
  }
`

export const SaveProjectSettingMutation = gql`
  mutation createOrUpdateProjects($attributes: ProjectInput!) {
    createOrUpdateProjects(attributes: $attributes) {
      project {
        id
        min_grouping_welds_length
      }
      errors
    }
  }
`

export const WeldNumericFilterValuesQuery = gql`
  query WeldNumericFilterValues($project_id: ID!, $column: String!, $include_project_descendants_welds: Boolean) {
    weld_numeric_filter_values(project_id: $project_id, column: $column, include_project_descendants_welds: $include_project_descendants_welds)
  }
`



// WEBPACK FOOTER //
// ./src/components/Projects/mutations.js